/**
 * OpenBulkPRDialog — bulk-open PR for N selected prompt drafts.
 *
 * Hits the smart endpoint at ``prompts/open-pr/bulk/from-drafts``
 * (PROC-102) for any selection — all-new, all-existing, or mixed.
 * Backend partitions per-entry: existing keys get surgical yaml
 * edits, missing keys get appended to their per-row target yaml (an
 * existing file or a brand-new one), plus one shared data migration
 * for the new-row inserts. One paired pipelines + api-django PR.
 *
 * Per-row target yaml selection: each NEW prompt carries its own
 * target file so a batch can split N new prompts across M yaml
 * files (existing or newly-created). Existing prompts stay in
 * whatever file already owns them and ignore the target UI.
 */
import { XMarkIcon } from "@heroicons/react/24/outline";
import { type FC, useEffect, useMemo, useRef, useState } from "react";

import { Button, Small } from "@fronterahealth/frontera-ui-components";

import { BASECAMP_API_BASE } from "@api/rest/api-fetch";
import { customFetch } from "@api/rest/custom-fetcher";
import { useListPromptYamlFiles } from "@api/rest/types-and-hooks";

interface BulkPROutput {
  pipelines_pr_url: string;
  api_django_pr_url: string;
  prompt_keys: string[];
}

interface OpenBulkPRDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPrompts: Array<{ key: string; name: string; registryStatus: "matched" | "missing" }>;
  onSuccess?: (result: BulkPROutput) => void;
}

/**
 * Compact per-row picker: choose an existing yaml file OR type a
 * new filename. Two visual modes — dropdown for existing, text
 * input for "custom" — toggleable via the dropdown's "+ New file…"
 * option and a small "Cancel new" button.
 */
interface TargetPickerProps {
  value: string;
  onChange: (value: string) => void;
  existingFiles: string[];
  isLoading: boolean;
}
const NEW_FILE_SENTINEL = "__new__";
const TargetPicker: FC<TargetPickerProps> = ({ value, onChange, existingFiles, isLoading }) => {
  // ``customMode`` = user has picked "+ New file…" and is typing a
  // filename. Kept as component state (not derived from value)
  // because an empty custom filename still needs to render an input,
  // not a dropdown.
  const [customMode, setCustomMode] = useState(false);

  if (customMode) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={value}
          placeholder="new_file.yaml"
          onChange={(e) => onChange(e.target.value)}
          className="w-40 rounded border border-border-medium bg-bg-surface px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-limestone-500"
        />
        <button
          type="button"
          onClick={() => {
            setCustomMode(false);
            onChange("");
          }}
          className="text-xs text-text-tertiary hover:text-text-secondary"
          aria-label="Cancel new file"
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === NEW_FILE_SENTINEL) {
          setCustomMode(true);
          onChange("");
        } else {
          onChange(v);
        }
      }}
      className="w-40 rounded border border-border-medium bg-bg-surface px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-limestone-500"
      disabled={isLoading}
    >
      <option value="">{isLoading ? "Loading…" : "Pick yaml…"}</option>
      {existingFiles.map((f) => (
        <option key={f} value={f}>
          {f}
        </option>
      ))}
      <option value={NEW_FILE_SENTINEL}>+ New file…</option>
    </select>
  );
};

export const OpenBulkPRDialog: FC<OpenBulkPRDialogProps> = ({ isOpen, onClose, selectedPrompts, onSuccess }) => {
  // Per-new-prompt target yaml file. Existing prompts ignore this
  // map entirely (backend discovers their owning file).
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkPROutput | null>(null);

  const cancelledRef = useRef(false);
  useEffect(() => {
    // Only reset the cancelled ref on open — state is already cleared
    // by ``handleClose`` when the dialog closed, and setState inside
    // useEffect trips ``react-hooks/set-state-in-effect``. Refs are
    // fine because they don't schedule a render.
    if (isOpen) cancelledRef.current = false;
  }, [isOpen]);

  const { matchedKeys, missingKeys, sortedPrompts } = useMemo(() => {
    const matched: string[] = [];
    const missing: string[] = [];
    for (const p of selectedPrompts) {
      if (p.registryStatus === "matched") matched.push(p.key);
      else missing.push(p.key);
    }
    const sorted = [
      ...selectedPrompts.filter((p) => p.registryStatus === "matched"),
      ...selectedPrompts.filter((p) => p.registryStatus === "missing"),
    ];
    return { matchedKeys: matched, missingKeys: missing, sortedPrompts: sorted };
  }, [selectedPrompts]);
  const hasNew = missingKeys.length > 0;
  const hasExisting = matchedKeys.length > 0;

  const yamlFilesQuery = useListPromptYamlFiles({
    query: { enabled: isOpen && hasNew },
  });
  const yamlFiles = yamlFilesQuery.data?.status === 200 ? yamlFilesQuery.data.data.filenames : [];

  // "Ready" iff every new prompt has a non-blank target filename.
  // Whitespace-only is treated as blank so users can't slip an empty
  // filename through via a space.
  const allNewHaveTargets = missingKeys.every((k) => (targets[k] || "").trim().length > 0);

  const handleClose = () => {
    cancelledRef.current = true;
    setTargets({});
    setError(null);
    setResult(null);
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (selectedPrompts.length === 0) return;
    if (hasNew && !allNewHaveTargets) return;

    setIsSubmitting(true);
    setError(null);
    try {
      // Only send target_yaml_filename on new (missing) entries. The
      // backend ignores it for matched entries, but keeping the wire
      // format clean helps debugging.
      const body = {
        prompts: selectedPrompts.map((p) => ({
          prompt_key: p.key,
          ...(p.registryStatus === "missing" ? { target_yaml_filename: targets[p.key].trim() } : {}),
        })),
      };
      const res = await customFetch<{
        data: BulkPROutput | { detail?: string };
        status: number;
      }>(`${BASECAMP_API_BASE}/prompts/open-pr/bulk/from-drafts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (cancelledRef.current) return;
      if (res.status < 200 || res.status >= 300) {
        const detail = (res.data as { detail?: string }).detail;
        setError(detail ?? `Failed to open bulk PR (HTTP ${res.status}).`);
        return;
      }
      const data = res.data as BulkPROutput;
      setResult(data);
      onSuccess?.(data);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e.message : "Failed to open bulk PR.");
    } finally {
      if (!cancelledRef.current) setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const title = result
    ? "PRs opened"
    : hasNew && hasExisting
      ? `Open PR — ${missingKeys.length} new + ${matchedKeys.length} edited`
      : hasNew
        ? `Open PR for ${missingKeys.length} new prompts`
        : `Open edit PR for ${matchedKeys.length} existing prompts`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleClose}>
      <div className="bg-bg-surface rounded-lg shadow-xl w-full max-w-xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
          <button onClick={handleClose} aria-label="Close">
            <XMarkIcon className="h-5 w-5 text-text-secondary" />
          </button>
        </div>

        {result ? (
          <div className="space-y-3">
            <Small className="text-text-secondary">
              One paired pipelines + api-django PR contains all {result.prompt_keys.length} prompts. Review both:
            </Small>
            <div className="space-y-2">
              <a
                href={result.pipelines_pr_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-rio-600 hover:text-rio-700 hover:underline break-all"
              >
                Pipelines PR → {result.pipelines_pr_url}
              </a>
              <a
                href={result.api_django_pr_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-rio-600 hover:text-rio-700 hover:underline break-all"
              >
                api-django PR → {result.api_django_pr_url}
              </a>
            </div>
            <div className="flex justify-end pt-2">
              <Button text="Done" appearance="primary" onClick={handleClose} />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <ul className="max-h-64 overflow-y-auto text-sm text-text-primary bg-limestone-50 rounded px-3 py-2 space-y-1">
              {sortedPrompts.map((p) => (
                <li key={p.key} className="flex items-center gap-2">
                  <div className="truncate min-w-0 flex-1">
                    <span className="font-medium">{p.name}</span> <span className="text-text-secondary">({p.key})</span>
                  </div>
                  {p.registryStatus === "missing" ? (
                    <>
                      <span className="shrink-0 text-xs text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">new</span>
                      <TargetPicker
                        value={targets[p.key] ?? ""}
                        onChange={(v) => setTargets((prev) => ({ ...prev, [p.key]: v }))}
                        existingFiles={yamlFiles}
                        isLoading={yamlFilesQuery.isLoading}
                      />
                    </>
                  ) : null}
                </li>
              ))}
            </ul>

            {error && (
              <div className="rounded bg-rio-50 border border-rio-200 px-3 py-2 text-sm text-rio-700">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button text="Cancel" appearance="secondary" onClick={handleClose} />
              <Button
                text={isSubmitting ? "Opening PR…" : "Open PR"}
                appearance="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || selectedPrompts.length === 0 || (hasNew && !allNewHaveTargets)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
